<script setup lang="ts">
import { ref } from 'vue'
import { FileText, Loader2, Upload } from 'lucide-vue-next'
import SectionHead from './SectionHead.vue'
import { useAuth } from '../../../composables/useAuth'
import { API_CONFIG } from '../../../config/api'

export interface ProfileDoc { id: string; title: string; file_name: string; file_size: number; mime_type: string; created_at: string }

const props = defineProps<{ supplierId: string; docs: ProfileDoc[]; canEdit: boolean }>()
const emit = defineEmits<{ changed: [] }>()
const { getAccessTokenSilently } = useAuth()

const MAX_BYTES = 25 * 1024 * 1024
const ALLOWED = /\.(pdf|docx|pptx)$/i
const uploading = ref(false)
const error = ref('')

// Same endpoints SupplierDocumentsCard uses in manage mode.
const url = (path = '') => `${API_CONFIG.baseURL}/api/suppliers/${props.supplierId}/documents${path}`
async function authed(path = '', init: RequestInit = {}) {
  const token = await getAccessTokenSilently()
  return fetch(url(path), { ...init, headers: { ...(init.headers || {}), Authorization: `Bearer ${token}` } })
}

async function onPick(ev: Event) {
  const input = ev.target as HTMLInputElement
  const file = input.files?.[0]
  input.value = ''
  if (!file) return
  error.value = ''
  if (!ALLOWED.test(file.name)) { error.value = 'Upload a PDF, DOCX or PPTX file.'; return }
  if (file.size > MAX_BYTES) { error.value = 'Files must be 25 MB or smaller.'; return }
  uploading.value = true
  try {
    const fd = new FormData()
    fd.append('file', file)
    const res = await authed('', { method: 'POST', body: fd })
    if (!res.ok) { const j = await res.json().catch(() => ({})); error.value = j.error || 'Upload failed'; return }
    emit('changed')
  } catch { error.value = 'Upload failed' }
  finally { uploading.value = false }
}

async function open(doc: ProfileDoc) {
  try {
    const j = await (await authed(`/${doc.id}/download`)).json()
    if (j.url) window.open(j.url, '_blank')
  } catch (e) { console.error('Download failed', e) }
}

const fileType = (d: ProfileDoc) => (d.file_name.split('.').pop() || '').toUpperCase()
const fmtSize = (b: number) => b >= 1048576 ? `${(b / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(b / 1024))} KB`
const fmtDate = (d: string) => new Date(d).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })
</script>

<template>
  <section aria-labelledby="csp-documents">
    <SectionHead id="csp-documents" title="Documents" :count="docs.length">
      <label v-if="canEdit && docs.length" class="relative csp-link inline-flex items-center gap-1 cursor-pointer">
        <Upload :size="13" aria-hidden="true" /> Upload
        <input type="file" class="sr-only" accept=".pdf,.docx,.pptx" :disabled="uploading" @change="onPick" />
      </label>
    </SectionHead>
    <p v-if="error" role="alert" class="text-[13px] text-red-600 mb-2">{{ error }}</p>

    <div v-if="!docs.length" class="rounded-[6px] border border-(--csp-border) bg-(--csp-bg) px-5 py-6 text-center">
      <p class="text-sm font-semibold text-(--csp-text)">No documents uploaded</p>
      <p class="text-[13px] csp-muted mt-1">
        {{ canEdit ? 'Datasheets, one-pagers and case studies appear here for advisors. PDF, DOCX or PPTX up to 25 MB.' : 'This supplier hasn’t shared any documents yet.' }}
      </p>
      <label v-if="canEdit" class="relative csp-btn mt-3 cursor-pointer">
        <Loader2 v-if="uploading" :size="14" class="animate-spin" aria-hidden="true" />
        <Upload v-else :size="14" aria-hidden="true" />
        {{ uploading ? 'Uploading…' : 'Upload' }}
        <input type="file" class="sr-only" accept=".pdf,.docx,.pptx" :disabled="uploading" @change="onPick" />
      </label>
    </div>

    <ul v-else>
      <li v-for="d in docs" :key="d.id" class="csp-row flex items-center gap-3 border-b border-(--csp-divider) last:border-b-0">
        <FileText :size="16" class="csp-muted shrink-0" aria-hidden="true" />
        <div class="min-w-0 flex-1">
          <p class="font-semibold text-(--csp-text) truncate">{{ d.title }}</p>
          <p class="text-[13px] csp-muted">{{ fileType(d) }} · {{ fmtSize(d.file_size) }} · {{ fmtDate(d.created_at) }}</p>
        </div>
        <button type="button" class="csp-link text-[13px] shrink-0" :aria-label="`Open ${d.title}`" @click="open(d)">Open</button>
      </li>
    </ul>
  </section>
</template>
